/**
 * InvoiceService
 *
 * This service handles all invoice-related operations in the application.
 * It provides methods for managing invoices, including creation, retrieval,
 * updating, and deletion of invoice entries. The service supports operations
 * for both individual matters and system-wide invoice management.
 *
 * @class
 */

import {
  CreateInvoiceRequest,
  UpdateInvoiceRequest,
  GetInvoiceResponse,
  GetInvoicesResponse,
  GetInvoiceFromResponse,
  CreateInvoiceResponse,
  PutInvoiceResponse,
  DeleteInvoiceResponse,
  VoidInvoiceResponse,
  CreatePaymentLinkDataRequest,
  CreatePaymentLinkDataResponse,
} from '@frontend/Models/Invoice/InvoiceRequestResponseModels';
import HttpService from './HttpService';
import {
  pathInvoices,
  pathInvoiceId,
  pathInvoiceFrom,
  pathInvoicesByMatterId,
  pathCreatePaymentLink,
  pathUninvoicedSummary,
  pathUninvoicedItems,
} from '@frontend/__settings__/septSoftwarePaths';

const http = new HttpService();

class InvoiceService {
  /**
   * Retrieves invoice sender information
   *
   * @returns {Promise<Object>} Invoice sender details
   * @throws {Error} If the API request fails
   */
  static async getInvoiceFrom(): Promise<GetInvoiceFromResponse> {
    const response = await http.get<GetInvoiceFromResponse | { data?: GetInvoiceFromResponse }>(pathInvoiceFrom);
    if (Array.isArray(response)) return response;
    const data = (response as { data?: GetInvoiceFromResponse }).data;
    if (!Array.isArray(data)) {
      throw new Error('Unexpected response shape from invoice-from endpoint');
    }
    return data;
  }

  /**
   * Creates a new invoice
   *
   * @param {Object} data - Invoice data
   * @param {number} [data.acc_rec_total=0] - Total accounts receivable
   * @param {number} [data.acc_pay_total=0] - Total accounts payable
   * @param {Array} [data.line_items] - Array of line items for the invoice
   *
   * @returns {Promise<Object>} Created invoice object
   * @throws {Error} If the API request fails
   */
  static async createInvoice(data: CreateInvoiceRequest): Promise<CreateInvoiceResponse> {
    const invoiceData = {
      ...data,
      acc_rec_total: data.acc_rec_total ?? 0,
      acc_pay_total: data.acc_pay_total ?? 0,
      line_items: Array.isArray(data.line_items) ? data.line_items : data.line_items ?? [],
    };

    const response = await http.post<any>(`${pathInvoices}`, invoiceData);
    const inner = response?.data ?? response;
    return inner;
  }

  /**
   * Retrieves an invoice by its ID
   *
   * @param {string} invoiceId - ID of the invoice
   * @returns {Promise<Object>} Invoice details
   * @throws {Error} If the API request fails
   */
  static async getInvoiceById(invoiceId: string): Promise<GetInvoiceResponse> {
    const response = await http.get<any>(`${pathInvoiceId(invoiceId)}`);
    const invoice = response?.data ?? response;
    if (invoice.line_items && typeof invoice.line_items === 'string') {
      try {
        invoice.line_items = JSON.parse(invoice.line_items);
      } catch (parseError) {
        console.error('Error parsing line_items JSON:', parseError);
        invoice.line_items = [];
      }
    } else if (!invoice.line_items) {
      invoice.line_items = [];
    }
    return invoice;
  }

  /**
   * Retrieves all invoices from the system
   *
   * @returns {Promise<Array>} Array of invoice objects
   * @throws {Error} If the API request fails
   */
  static async getAllInvoices(): Promise<GetInvoicesResponse> {
    const response = await http.get<GetInvoicesResponse | { data?: GetInvoicesResponse }>(`${pathInvoices}`);
    if (Array.isArray(response)) {
      return response;
    }
    const data = (response as { data?: GetInvoicesResponse }).data;
    return Array.isArray(data) ? data : [];
  }

  /**
   * Retrieves invoices for a specific matter
   *
   * @param {string} matterId - ID of the matter
   * @returns {Promise<Array>} Array of invoices for the matter
   * @throws {Error} If the API request fails
   */
  static async getInvoicesByMatterId(matterId: string): Promise<GetInvoicesResponse> {
      const response = await http.get<GetInvoicesResponse | { data?: GetInvoicesResponse }>(pathInvoicesByMatterId(matterId));
      if (Array.isArray(response)) {
        return response;
      }
      const data = (response as { data?: GetInvoicesResponse }).data;
      return Array.isArray(data) ? data : [];
  }

  /**
   * Updates an existing invoice
   *
   * @param {Object} data - Updated invoice data
   * @param {string} data.invoiceid - ID of the invoice to update
   * @param {Array} [data.line_items] - Array of line items for the invoice
   *
   * @returns {Promise<Object>} Updated invoice object
   * @throws {Error} If the API request fails
   */
  static async updateInvoice(data: UpdateInvoiceRequest): Promise<PutInvoiceResponse> {
    const invoiceData = { ...data };

    const response = await http.put<PutInvoiceResponse>(`${pathInvoiceId(data.invoiceid)}`, invoiceData);
    return response;
  }

  /**
   * Deletes an invoice
   *
   * @param {string} invoiceId - ID of the invoice to delete
   * @returns {Promise<Object>} Response indicating success or failure
   * @throws {Error} If the API request fails
   */
  static async deleteInvoice(invoiceId: string): Promise<DeleteInvoiceResponse> {
    const response = await http.delete<DeleteInvoiceResponse>(`${pathInvoiceId(invoiceId)}`);
    return response;
  }

  static async voidInvoice(invoiceId: string): Promise<VoidInvoiceResponse> {
    const response = await http.post<VoidInvoiceResponse>(`${pathInvoiceId(invoiceId)}/void`, {});
    return response;
  }

  /**
   * the method creates a payment link for a specific invoice
   *
   * @param paymentLinkData the payment link data
   * @returns a promise that resolves to an object containing the payment link
   */
  static async createPaymentLink(paymentLinkData: CreatePaymentLinkDataRequest): Promise<CreatePaymentLinkDataResponse> {
    const response = await http.post<CreatePaymentLinkDataResponse>(pathCreatePaymentLink, paymentLinkData);
    return response;
  }

  static async getUninvoicedSummary(filters?: UninvoicedFilters): Promise<UninvoicedSummaryClient[]> {
    const response = await http.get<UninvoicedSummaryClient[] | { data?: UninvoicedSummaryClient[] }>(pathUninvoicedSummary + uninvoicedQuery(filters));
    if (Array.isArray(response)) return response;
    const data = (response as { data?: UninvoicedSummaryClient[] }).data;
    return Array.isArray(data) ? data : [];
  }

  static async getUninvoicedItems(matterId: string, filters?: UninvoicedFilters): Promise<UninvoicedItem[]> {
    const response = await http.get<UninvoicedItem[] | { data?: UninvoicedItem[] }>(pathUninvoicedItems(matterId) + uninvoicedQuery(filters));
    if (Array.isArray(response)) return response;
    const data = (response as { data?: UninvoicedItem[] }).data;
    return Array.isArray(data) ? data : [];
  }
}

export interface UninvoicedBreakdown {
  count: number;
  total: number;
}

export interface UninvoicedMatter {
  matterid: string;
  matter_name: string;
  uninvoiced_count: number;
  uninvoiced_total: number;
  breakdown: Record<string, UninvoicedBreakdown>;
  project_number?: string;
  leader_id?: string;
  leader_name?: string;
  area?: string;
  status?: string;
  oldest_date?: string | null;
  periods?: { date: string | null; source_type: string; count: number; total: number }[];
}

export interface UninvoicedFilters {
  non_pipeline?: boolean;
  start_date?: string;
  end_date?: string;
  category?: string;
  include_undated?: boolean;
}

export interface UninvoicedInvoiceScope extends UninvoicedFilters {
  matterIds: string[];
  undatedOnly?: boolean;
}

function uninvoicedQuery(filters?: UninvoicedFilters): string {
  const query = new URLSearchParams();
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== '') query.set(key, String(value));
  });
  const queryString = query.toString();
  return queryString ? `?${queryString}` : '';
}

export interface UninvoicedSummaryClient {
  clientid: string;
  client_name: string;
  total_uninvoiced: number;
  matters: UninvoicedMatter[];
}

export interface UninvoicedItem {
  id: string;
  source_type: string;
  description: string;
  amount: number;
  date: string | null;
  extra: string;
  employee_id?: string | null;
  employee_name?: string | null;
}

export default InvoiceService;
